import React, { useEffect, useRef, useState } from "react";
import { CartDelivery, CartItem, Heading, Icon, Link, Notification, ShoppingCart, Button } from "@telia/teddy";
import type { HighlightedLineProps } from "@telia/teddy/dist/components/shopping-cart/shopping-cart-payment";
import { ExpandableTextContainer } from "../../../../components/components/molecules/ExpandableTextContainer";
import _get from "lodash/get";
import _isEmpty from "lodash/isEmpty";
import { connect, useSelector, useDispatch } from "react-redux";
import { Realm, PAYMENT_METHOD, PaymentMethod, REALM, DELIVERY_TYPE, DeliveryType } from "../../../../../../constants";
import PAGES from "../../../../../../constants/pages";
import {
    RootState,
    ICartItem,
    ICartSubscription,
    ICartDeliveryOption,
    CART_ITEM_TYPE,
    ICartItemWithBundleId,
} from "../../../../../../models";
import { Disclaimer, OfferingState } from "../../../../../../models/state";
import { getFormattedPrice } from "../../../../../../shared/priceFormatter";
import { isRunningOnClientSide } from "../../../../../../shared/util";
import {
    getRealm,
    isSummary as isSummarySelector,
    isCheckout as isCheckoutSelector,
    isTransfer as isTransferSelector,
    isOrderValid as isOrderValidSelector,
} from "../../../../app/state/selectors";
import { API_URL_GET_ORDER_SUMMARY } from "../../../../checkout/api";
import EmptyCartModal from "../../../../checkout/components/molecules/EmptyCartModal/EmptyCartModal";
import SummaryContinueButton from "../../../../checkout/components/organisms/SummaryContinueButton/SummaryContinueButton";
import {
    changeAccessoryQuantity,
    emptyCart,
    removeItemFromCart,
    trackInitiateCheckout,
} from "../../../../checkout/state/actions/cartActions";
import {
    getCartContentPreparedForRealm,
    getFirstBundleItemInCart,
    getNumberOfItemsInCart,
    getTotalPriceFirstInvoice,
    getTotalPriceMonthly,
    getTotalPriceUpfront,
    getTotalPriceUpfrontWithoutVAT,
    hasPaid as hasPaidSelector,
    getMobileRefundDisclaimer,
} from "../../../../checkout/state/selectors/cartSelectors";
import {
    getMonthlyPriceDetails as getMonthlyPriceDetailsSelector,
    getTotalDisclaimerPrices as getTotalDisclaimerPricesSelector,
    getPaymentTooltip as getPaymentTooltipSelector,
    isDigitalDelivery as isDigitalDeliverySelector,
    isNewPhysicalSimRequired,
    isSimOnly,
} from "../../../../checkout/state/selectors/combinedSelectors";
import {
    getDeliveryDescription,
    getDeliveryInfoType,
    getEnhancedDeliveryInfoSelected,
} from "../../../../checkout/state/selectors/deliveryInfoSelectors";
import { getDisclaimers } from "../../../../checkout/state/selectors/disclaimerSelectors";
import { getOfferingsForCart } from "../../../../checkout/state/selectors/offeringsSelectors";
import { getPaymentSelectedType } from "../../../../checkout/state/selectors/paymentSelectors";
import {
    getLegalSubscription,
    getLegalSubscriptionId,
} from "../../../../checkout/state/selectors/subscriptionsSelectors";
import CHECKOUT_TEXT from "../../../../checkout/text-consts";
import { getListingPageForItem } from "../../../../checkout/utils";
import { getComboProductName } from "../../../../google-analytics";
import { shouldShowGoToCartButton as shouldShowGoToCartButtonSelector } from "../../../state/selectors";
import { SHOPPING_CART_RECEIPT, SHOPPING_CART } from "../../../text-consts";
import "./ConnectedShoppingCart.less";

const HW_TYPE_TO_DISCOUNT_TYPE = {
    [CART_ITEM_TYPE.HANDSET]: "Telefonrabatten",
    [CART_ITEM_TYPE["HANDSET-PREOWNED"]]: "Telefonrabatten",
    [CART_ITEM_TYPE.WEARABLE]: "Klokkerabatten",
    [CART_ITEM_TYPE.TABLET]: "Nettbrettrabatten",
    [CART_ITEM_TYPE.ACCESSORY]: "Tilbehørsrabatten",
} as const;

export const getHWTextForTooltip = (type?: keyof typeof HW_TYPE_TO_DISCOUNT_TYPE): string =>
    (type && HW_TYPE_TO_DISCOUNT_TYPE[type]) || "Prisen";

interface StateProps {
    cart: ICartItem[];
    deliveryDescription: string;
    deliveryInfoType?: DeliveryType;
    disclaimers: Disclaimer[];
    firstBundleItem?: ICartItemWithBundleId;
    hasPaid: boolean;
    isCheckout: boolean;
    isDigitalDelivery: boolean;
    isOrderValid: boolean;
    isPhysicalSimRequired: boolean;
    isSimOnlyInCart: boolean;
    isSummary: boolean;
    isTransfer: boolean;
    legalSubscription?: ICartSubscription;
    legalSubscriptionId?: string;
    mobileRefundDisclaimer?: HighlightedLineProps;
    monthlyPriceDetails?: {
        label: string;
        value: string;
    }[];
    numberOfCartItems: number;
    offeringsForCart: OfferingState;
    realm: Realm;
    selectedDeliveryMethod?: ICartDeliveryOption;
    selectedPaymentType?: PaymentMethod;
    shouldShowGoToCartButton: boolean;
    showPaymentTooltip: boolean;
    totalDisclaimerPrice?: any;
    // Change type to string | DisclaimerItem[] after updating Styleguide
    totalPriceFirstInvoice: number;
    totalPriceMonthly: number;
    totalPriceUpfront: number;
    totalPriceWithoutVAT: number;
    totalVAT: number;
}

interface ConnectedShoppingCartProps extends StateProps {
    heading?: string;
    isReadonly?: boolean;
    isRetailer?: boolean;
    useStandalone?: boolean;
    shouldShowTeliaTryggInfo?: boolean;
}

const DownloadCartButton = () => (
    <Link
        className="download-shoppingcart-button"
        href={API_URL_GET_ORDER_SUMMARY}
        target="_blank"
        variant="text"
        aria-label="Last ned ordreoppsummering (åpnes i nytt vindu)"
    >
        <Icon name="document-pdf" size="sm" aria-hidden="true" focusable="false" />
        Last ned ordreoppsummering
    </Link>
);

export const TeliaTryggCard: React.FC = () => (
    <Notification variant="gray" mb="300">
        <Notification.Heading as="h3">
            <Notification.Icon name="secured-1">Telia Trygg</Notification.Icon>
        </Notification.Heading>
        <Notification.Text>
            Du vil snart motta en e-post med aktiveringslenke. <br />
            Fullfør oppsettet for å ta tjenesten i bruk.
        </Notification.Text>
    </Notification>
);

const ConnectedShoppingCart: React.FC<ConnectedShoppingCartProps> = ({
    realm,
    cart,
    numberOfCartItems,
    totalPriceFirstInvoice,
    totalPriceMonthly,
    monthlyPriceDetails,
    totalPriceUpfront,
    totalVAT,
    totalPriceWithoutVAT,
    totalDisclaimerPrice,
    useStandalone = false,
    isRetailer = false,
    isReadonly = false,
    hasPaid,
    isDigitalDelivery,
    isPhysicalSimRequired,
    isSimOnlyInCart,
    isCheckout,
    isSummary,
    isOrderValid,
    isTransfer,
    legalSubscriptionId,
    selectedDeliveryMethod,
    selectedPaymentType,
    showPaymentTooltip,
    offeringsForCart,
    legalSubscription,
    firstBundleItem,
    disclaimers,
    deliveryInfoType,
    deliveryDescription,
    heading: headingProp,
    mobileRefundDisclaimer,
    shouldShowTeliaTryggInfo,
}) => {
    const dispatch = useDispatch();
    const [lastRemovedItem, setLastRemovedItem] = useState<{ action: string; item: ICartItem } | undefined>(undefined);
    const [isClientSide, setIsClientSide] = useState(false);
    const hwTextForTooltip = `${getHWTextForTooltip(_get(firstBundleItem, "type"))}`;
    const cartRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setIsClientSide(isRunningOnClientSide());
    }, []);

    // Update global optimize variable on each render (in case cart has changed)
    const gaComboProductName = useSelector((state: RootState) => getComboProductName(state));
    if (isClientSide) {
        window.comboProductName = gaComboProductName;
    }

    function removeLastItem(): void {
        dispatch(emptyCart()).then(() => {
            setLastRemovedItem(undefined);
            if (!isRetailer) {
                const listingPageURL = getListingPageForItem(_get(lastRemovedItem, "item"));
                window.location.assign(listingPageURL);
            }
        });
    }

    function onChangeQuantity(item: ICartItem, quantity: number): void {
        if (item.type === "ACCESSORY") {
            dispatch(changeAccessoryQuantity(item.id, quantity, item.discount?.smartDiscountCode));
        }
    }

    function onRemoveItem(itemToRemove: ICartItem): void {
        // If item will empty the cart, set lastRemovedItem to this item, so we can show modal and remove it after confirmation
        // Find all subItems of itemToRemove. SubItems are indented items that comes directly after itemToRemove.
        const subItems: ICartItem[] = [];
        let hasPassedItemToRemove = false;
        for (let index = 0; index < cart.length; index++) {
            const item = cart[index];

            if (!hasPassedItemToRemove && item === itemToRemove) {
                hasPassedItemToRemove = true;
                continue;
            }

            if (!hasPassedItemToRemove) {
                continue;
            }

            if (!item.indent) {
                break;
            }
            subItems.push(item);
        }

        const willEmptyCart =
            cart
                .filter((cartItem) => !subItems.includes(cartItem))
                .filter((cartItem) => cartItem.type !== CART_ITEM_TYPE.DELIVERY)
                .filter((cartItem) => cartItem.bundleId || cartItem.id !== itemToRemove.id)
                .filter((cartItem) => !cartItem.bundleId || cartItem.bundleId !== itemToRemove.bundleId).length === 0;

        if (willEmptyCart && !isRetailer) {
            setLastRemovedItem({ action: "REMOVED_LAST_ITEM", item: itemToRemove });
            return;
        }

        // If item is a filled out legal subscription then also show modal
        const isLegalSubscription = itemToRemove.bundleId === legalSubscriptionId;
        const isLegalGoingToBeRemoved =
            isLegalSubscription &&
            (itemToRemove.type === CART_ITEM_TYPE.SUBSCRIPTION ||
                itemToRemove.type === CART_ITEM_TYPE.SUBSCRIPTION_DRAFT);

        // TODO: Remove this error when checkout package has been migrated to TS
        // oxlint-disable-next-line typescript/ban-ts-comment
        // @ts-expect-error
        dispatch(removeItemFromCart(itemToRemove, isLegalGoingToBeRemoved));
    }

    function onGoToCart(): void {
        dispatch(trackInitiateCheckout());
        return window.location.assign(PAGES.CHECKOUT.PERSONAL_DETAILS.URL);
    }

    // Add handsetDiscount field to handset item
    if (useStandalone && !_isEmpty(offeringsForCart) && legalSubscription) {
        // TODO: Remove this error when checkout package has been migrated to TS
        // oxlint-disable-next-line typescript/ban-ts-comment
        // @ts-expect-error
        const selectedOffering = offeringsForCart.find((offering) => offering.offeringCode === legalSubscription.code);
        const handsetDiscount = _get(selectedOffering, "discount");

        if (handsetDiscount && _get(cart, "[0].type") === CART_ITEM_TYPE.HANDSET) {
            const handsetDiscountText = `Telefonrabatt med <br /> ${legalSubscription.name}: <span style='color:#008641;'>-${handsetDiscount},-</span>`;
            cart = cart.map((item) => {
                if (item.type === CART_ITEM_TYPE.HANDSET) {
                    return {
                        ...item,
                        discount: {
                            ...item.discount,
                            value: {
                                upfront: item.discount?.value.upfront || 0,
                                monthly: item.discount?.value.monthly || 0,
                            },
                            handsetDiscountText,
                        },
                    };
                }
                return item;
            });
        }
    }

    function renderEmptyCartModal(event: { action: string; item: ICartItem }): JSX.Element {
        switch (_get(event, "action")) {
            case "REMOVED_LAST_ITEM":
                return (
                    <EmptyCartModal
                        title={CHECKOUT_TEXT.MODAL.EMPTY_CART.HEADING}
                        content={CHECKOUT_TEXT.MODAL.EMPTY_CART.DESCRIPTION}
                        onSubmit={(): void => {
                            removeLastItem();
                        }}
                        onClose={(): void => {
                            setLastRemovedItem(undefined);
                        }}
                    />
                );
            case "REMOVED_LEGAL":
                return (
                    <EmptyCartModal
                        title={CHECKOUT_TEXT.MODAL.REMOVE_LEGAL.HEADING}
                        content={CHECKOUT_TEXT.MODAL.REMOVE_LEGAL.DESCRIPTION}
                        onSubmit={(): void => {
                            removeLastItem();
                        }}
                        onClose={(): void => {
                            setLastRemovedItem(undefined);
                        }}
                    />
                );
            default:
                return <></>;
        }
    }

    let b2bFields = {};
    if (realm === REALM.B2B) {
        b2bFields = {
            totalVAT,
            totalPriceWithoutVAT,
        };
    }

    const defaultDeliveryValue = isSummary ? "Velges nå" : "Velges senere";
    const defaultDeliveryFields =
        !isDigitalDelivery && !isPhysicalSimRequired && !isSimOnlyInCart
            ? {
                  label: "Levering",
                  value: defaultDeliveryValue,
              }
            : !isDigitalDelivery && isSimOnlyInCart
              ? {
                    label: "Brev i postkassen",
                    value: "0,-",
                }
              : undefined;

    const getDeliveryFields = () => {
        if (isRetailer) return;
        if (deliveryInfoType === DELIVERY_TYPE.PICK_UP_IN_SHOP) {
            return isSimOnlyInCart && isPhysicalSimRequired
                ? { label: CHECKOUT_TEXT.DELIVERY.CLICK_AND_FETCH.HEADING, value: deliveryDescription }
                : undefined;
        }
        if (!selectedDeliveryMethod) return defaultDeliveryFields;

        return {
            label: selectedDeliveryMethod.label || "Levering",
            value:
                selectedDeliveryMethod.price !== undefined
                    ? `${selectedDeliveryMethod.pricePrefix || ""} ${selectedDeliveryMethod.price},-`.trim()
                    : defaultDeliveryValue,
            progressBar: undefined,
        };
    };

    const isFirstInvoicePayment = PAYMENT_METHOD.INVOICE === selectedPaymentType;
    const upfrontToPay = isFirstInvoicePayment ? 0 : totalPriceUpfront + _get(selectedDeliveryMethod, "price", 0);
    const firstInvoiceToPay = isFirstInvoicePayment
        ? totalPriceFirstInvoice + totalPriceMonthly + _get(selectedDeliveryMethod, "price", 0)
        : 0;
    const delivery: CartDelivery | undefined = getDeliveryFields();
    const heading = useStandalone ? (hasPaid ? SHOPPING_CART_RECEIPT : SHOPPING_CART(numberOfCartItems)) : "";
    const headingUsed = headingProp !== undefined ? headingProp : heading;

    if (isTransfer || numberOfCartItems <= 0) {
        return null;
    }

    return (
        <div className="connectedShoppingCart">
            {lastRemovedItem && !isRetailer && renderEmptyCartModal(lastRemovedItem)}
            {isClientSide && (
                <div>
                    <div ref={cartRef}>
                        {headingUsed && (
                            <Heading as="h2" variant="title-100" mb="200">
                                {headingUsed}
                            </Heading>
                        )}
                        {shouldShowTeliaTryggInfo && <TeliaTryggCard />}
                        <ShoppingCart
                            formatPrice={getFormattedPrice}
                            hasPaid={hasPaid}
                            isAllowedToDelete={!hasPaid && !isReadonly}
                        >
                            <ShoppingCart.Items
                                cartItems={cart as CartItem[]}
                                onChangeQuantity={onChangeQuantity as (item: CartItem, quantity: number) => void}
                                onRemoveItem={onRemoveItem as (item: CartItem) => void}
                                className="shopping-cart-items"
                            />
                            <ShoppingCart.Payment
                                delivery={delivery}
                                totalPriceFirstInvoice={firstInvoiceToPay}
                                totalPriceMonthly={totalPriceMonthly}
                                totalPriceUpfront={upfrontToPay}
                                paymentTooltip={
                                    showPaymentTooltip
                                        ? `<p class="paragraph"><span style="font-family:TeliaSans, Helvetica, Arial, sans-serif; font-weight:500;">Betale pr måned</span> <br/> ${hwTextForTooltip} varierer ut fra hvilket abonnement du velger og vil vises på neste side. </p>`
                                        : undefined
                                }
                                monthlyPriceDetails={monthlyPriceDetails}
                                highlightedLine={mobileRefundDisclaimer}
                                {...b2bFields}
                            />
                            {totalDisclaimerPrice && !isRetailer && (
                                <ShoppingCart.Disclaimer disclaimer={totalDisclaimerPrice} />
                            )}
                            {!useStandalone && !isCheckout && !isRetailer && (
                                <ShoppingCart.Continue>
                                    <Button onClick={onGoToCart} variant="primary">
                                        Gå til bestilling
                                    </Button>
                                </ShoppingCart.Continue>
                            )}
                        </ShoppingCart>
                    </div>
                </div>
            )}
            {isSummary && isOrderValid && !isRetailer && (
                <div className="download-shoppingcart-section">
                    <DownloadCartButton />
                    {!useStandalone && <SummaryContinueButton />}
                </div>
            )}
            {!_isEmpty(disclaimers) && <ExpandableTextContainer whiteBg textBlocks={disclaimers} />}
        </div>
    );
};

const mapStateToProps = (state: RootState): StateProps => ({
    realm: getRealm(state),
    cart: getCartContentPreparedForRealm(state, getRealm(state)),
    shouldShowGoToCartButton: shouldShowGoToCartButtonSelector(state),
    numberOfCartItems: getNumberOfItemsInCart(state),
    totalPriceFirstInvoice: getTotalPriceFirstInvoice(state),
    totalPriceMonthly: getTotalPriceMonthly(state),
    monthlyPriceDetails: getMonthlyPriceDetailsSelector(state),
    totalPriceUpfront: getTotalPriceUpfront(state),
    disclaimers: getDisclaimers(state),
    totalVAT: getTotalPriceUpfront(state) - getTotalPriceUpfrontWithoutVAT(state),
    totalPriceWithoutVAT: getTotalPriceUpfrontWithoutVAT(state),
    totalDisclaimerPrice: getTotalDisclaimerPricesSelector(state),
    hasPaid: hasPaidSelector(state),
    showPaymentTooltip: getPaymentTooltipSelector(state, _get(getLegalSubscription(state), "id", "")),
    firstBundleItem: getFirstBundleItemInCart(state),
    isDigitalDelivery: isDigitalDeliverySelector(state),
    isCheckout: isCheckoutSelector(state),
    isSummary: isSummarySelector(state),
    isOrderValid: isOrderValidSelector(state),
    isTransfer: isTransferSelector(state),
    legalSubscriptionId: getLegalSubscriptionId(state),
    selectedDeliveryMethod: getEnhancedDeliveryInfoSelected(state),
    selectedPaymentType: getPaymentSelectedType(state),
    offeringsForCart: getOfferingsForCart(state),
    legalSubscription: getLegalSubscription(state),
    isPhysicalSimRequired: isNewPhysicalSimRequired(state),
    isSimOnlyInCart: isSimOnly(state),
    deliveryInfoType: getDeliveryInfoType(state),
    deliveryDescription: getDeliveryDescription(state),
    mobileRefundDisclaimer: getMobileRefundDisclaimer(state),
});

export default connect(mapStateToProps)(ConnectedShoppingCart);
